import { Component, Input, Output, EventEmitter, forwardRef, HostListener, ElementRef, OnChanges, SimpleChanges, Injector, OnInit, inject } from '@angular/core'
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TypographyComponent } from '../typography'
import { IconComponent } from '../icon'
import { DropdownMenuComponent } from '../dropdown-menu'
import { DropdownMenuItemComponent } from '../dropdown-menu-item'
import { DropdownMenuSectionHeaderComponent } from '../dropdown-menu-section-header'
import { ErrorComponent } from '../error'

export interface SelectOption {
  id: string | number
  text: string
  originalObject?: any
  [key: string]: any
}

export type SelectState =
  | 'active'
  | 'hover'
  | 'focused'
  | 'filled'
  | 'inactive'
  | 'error'

@Component({
  selector: 'hyperkit-select',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TypographyComponent,
    IconComponent,
    DropdownMenuComponent,
    DropdownMenuSectionHeaderComponent,
    DropdownMenuItemComponent,
    ErrorComponent,
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  private injector = inject(Injector);
  private elementRef = inject(ElementRef);

  @Input() label = ''
  @Input() hint?: string
  @Input() placeholder = 'Select...'
  @Input() sectionHeader?: string
  @Input() disabled = false
  @Input() options: any[] = []
  @Input() displayProperty = 'name'
  @Input() valueProperty = 'id'
  @Input() dropdownPosition: 'fixed' | 'absolute' = 'fixed'

  @Output() selectionChange = new EventEmitter<any>()
  internalOptions: SelectOption[] = []
  selectedOption: SelectOption | null = null
  isOpen = false
  dropdownStyle: { [key: string]: string } = {}
  public ngControl: NgControl | null = null
  get selectedDisplayText(): string {
    return this.selectedOption ? this.selectedOption.text : ''
  }

  private pendingValue: any = null
  typedBuffer = ''
  bufferResetTimeout: any
  bufferResetDelay = 500
  private isHovering = false
  private originalState: SelectState = 'active'
  state: SelectState = 'active'

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null)
    if (this.ngControl) {
      this.ngControl.valueAccessor = this
    }
  }

  get isRequired(): boolean {
    return !!this.ngControl?.control?.hasValidator?.(Validators.required)
  }

  get hasError(): boolean {
    return !!this.ngControl?.invalid && !!this.ngControl?.touched
  }

  get errorMessage(): string {
    const errors = this.ngControl?.errors
    if (!errors) return ''

    if (errors['required']) return `${this.label || 'This field'} is required`

    return 'Invalid selection'
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled || !this.internalOptions.length) return

    const key = event.key
    if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
      this.typedBuffer += key.toLowerCase()
      clearTimeout(this.bufferResetTimeout)
      this.bufferResetTimeout = setTimeout(() => {
        this.typedBuffer = ''
      }, this.bufferResetDelay)

      this.jumpToBestMatch()
      event.preventDefault()
    }
  }

  private jumpToBestMatch(): void {
    if (!this.typedBuffer) return

    const match = this.internalOptions.find((option) =>
      option.text.toLowerCase().startsWith(this.typedBuffer),
    )

    if (match) {
      this.selectOption(match)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['options'] ||
      changes['displayProperty'] ||
      changes['valueProperty']
    ) {
      this.processOptions()
    }
  }

  private processOptions(): void {
    if (!Array.isArray(this.options)) {
      this.internalOptions = []
      return
    }

    this.internalOptions = this.options.map((item) => {
      if (item && typeof item === 'object' && 'id' in item && 'text' in item) {
        return item as SelectOption
      }

      if (typeof item === 'string') {
        return { id: item, text: item }
      }

      if (item && typeof item === 'object') {
        return {
          id: item[this.valueProperty],
          text: item[this.displayProperty]?.toString() || 'Unknown',
          originalObject: item,
        }
      }

      return { id: String(item), text: String(item) }
    })

    this.resolveSelectedOption()
  }

  private resolveSelectedOption(): void {
    if (this.pendingValue !== null && this.pendingValue !== undefined) {
      const match = this.internalOptions.find(
        (opt) => opt.id === this.pendingValue,
      )
      this.selectedOption = match || null

      this.state = match ? 'filled' : 'active'
    } else {
      this.selectedOption = null
      this.state = 'active'
    }
  }

  writeValue(value: any): void {
    this.pendingValue = value

    if (this.internalOptions.length) {
      this.resolveSelectedOption()
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  toggleDropdown(): void {
    if (this.disabled) return

    this.isOpen = !this.isOpen
    this.state = this.isOpen ? 'focused' : this.originalState
    if (this.isOpen) {
      this.onTouched()
      if (this.dropdownPosition === 'fixed') {
        this.calculateDropdownPosition()
      }
    }
  }

  private calculateDropdownPosition(): void {
    const selectField =
      this.elementRef.nativeElement.querySelector('.select-field')
    if (!selectField) return
    const rect = selectField.getBoundingClientRect()
    this.dropdownStyle = {
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    }
  }

  selectOption(option: SelectOption): void {
    this.selectedOption = option
    this.pendingValue = option.id
    this.isOpen = false

    this.onChange(option.id)
    this.onTouched()

    this.selectionChange.emit(option.originalObject || option.id)
    this.state = 'filled'
  }

  onMouseEnter(): void {
    if (this.disabled) return
    this.isHovering = true
    if (!['focused', 'inactive', 'error'].includes(this.state)) {
      this.originalState = this.state
      this.state = 'hover'
    }
  }
  onMouseLeave(): void {
    if (this.disabled) return
    this.isHovering = false
    if (this.state === 'hover') {
      this.state = this.originalState
    }
  }
  stopPropagation(event: Event): void {
    event.stopPropagation()
  }
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target) && this.isOpen) {
      this.isOpen = false
      this.state = this.originalState
      this.onTouched()
    }
  }
  private onChange: (value: any) => void = () => {}
  private onTouched: () => void = () => {}
}
